import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet } from 'Src/helpers/service/superadmin/api';

interface UserStat {
  userId: string;
  firstHit?: string;
  lastHit?: string;
  countHit?: number;
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    permissionList?: string[];
  };
}

const PAGE_SIZE = 50;

function getSchoolUserStats(
  schoolId: string,
  fromDate: string,
  toDate: string,
  skip: number,
  emailFilter?: string
): Promise<UserStat[]> {
  const where: any = {
    schoolId,
    startedAt: { $gte: fromDate, $lte: toDate }
  };
  if (emailFilter && emailFilter.trim()) {
    where.email = emailFilter.trim();
  }
  return superAdminGet('/stats/userpresence', {
    params: {
      filter: {
        where,
        limit: PAGE_SIZE,
        skip
      }
    }
  });
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function toDateInput(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

interface Props {
  schoolId: string;
}

interface State {
  isLoading: boolean;
  stats: UserStat[];
  errorMessage: string;
  fromDate: string;
  toDate: string;
  currentPage: number;
  hasMore: boolean;
  nameFilter: string;
}

export class SuperAdminSchoolUserStats extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      stats: [],
      errorMessage: '',
      fromDate: toDateInput(-7),
      toDate: toDateInput(0),
      currentPage: 0,
      hasMore: false,
      nameFilter: ''
    };
  }

  componentDidMount() {
    this.load(0);
  }

  load = (page: number) => {
    const { fromDate, toDate } = this.state;
    this.setState({ isLoading: true, errorMessage: '' });

    getSchoolUserStats(
      this.props.schoolId,
      new Date(fromDate).toISOString(),
      new Date(toDate + 'T23:59:59').toISOString(),
      page * PAGE_SIZE,
      this.state.nameFilter
    )
      .then(stats => {
        this.setState({
          isLoading: false,
          stats,
          currentPage: page,
          hasMore: stats.length === PAGE_SIZE
        });
      })
      .catch(err => {
        console.error('Cannot load user stats', err);
        this.setState({ isLoading: false, errorMessage: 'Could not load user stats.' });
      });
  };

  onApply = () => {
    this.load(0);
  };

  getFilteredStats(): UserStat[] {
    return this.state.stats;
  }

  render() {
    const { isLoading, errorMessage, fromDate, toDate, currentPage, hasMore, nameFilter } = this.state;
    const filtered = this.getFilteredStats();

    return (
      <div>
        <div className="mb-3">
          <h4 className="mb-1">User stats</h4>
          <div className="text-muted small">Login activity per user for the selected date range.</div>
        </div>

        {/* Controls */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
              <div>
                <label className="small font-weight-bold d-block mb-1">From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDate}
                  onChange={e => this.setState({ fromDate: e.target.value })}
                />
              </div>
              <div>
                <label className="small font-weight-bold d-block mb-1">To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDate}
                  onChange={e => this.setState({ toDate: e.target.value })}
                />
              </div>
              <div>
                <label className="small font-weight-bold d-block mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Email or name…"
                  value={nameFilter}
                  style={{ minWidth: 200 }}
                  onChange={e => this.setState({ nameFilter: e.target.value })}
                />
              </div>
              <button className="btn btn-primary btn-sm" onClick={this.onApply} disabled={isLoading}>
                {isLoading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        {isLoading ? (
          <Loader />
        ) : filtered.length === 0 ? (
          <div className="text-muted">No data for selected period.</div>
        ) : (
          <>
            <div className="text-muted small mb-2">
              Showing {filtered.length} user{filtered.length !== 1 ? 's' : ''}
              {currentPage > 0 ? `, page ${currentPage + 1}` : ''}
            </div>

            <table className="table table-sm table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>First hit</th>
                  <th>Last hit</th>
                  <th style={{ width: 80 }} className="text-center">
                    Hits
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.userId || i}>
                    <td className="small">{s.user?.email || '—'}</td>
                    <td className="small">{[s.user?.firstName, s.user?.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td className="small">
                      {(s.user?.permissionList || []).map(p => (
                        <span key={p} className="badge badge-secondary mr-1" style={{ fontSize: 10 }}>
                          {p}
                        </span>
                      ))}
                    </td>
                    <td className="small">{formatDateTime(s.firstHit)}</td>
                    <td className="small">{formatDateTime(s.lastHit)}</td>
                    <td className="text-center">
                      <strong className={s.countHit ? 'text-success' : 'text-muted'}>{s.countHit ?? 0}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage - 1)}
                disabled={currentPage === 0 || isLoading}
              >
                ← Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage + 1)}
                disabled={!hasMore || isLoading}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
}
